"use client";

import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { findLastIndex } from "../scrolling";

const VISIBLE_ITEM_RATIO = 0.75;

const useSliderItems = (scrollRef: RefObject<HTMLDivElement | null>) => {
  const itemsRef = useRef(new Set<HTMLDivElement>());
  const visibleItemsRef = useRef(new Set<Element>());
  const [itemCount, setItemCount] = useState(0);
  const [firstVisibleIndex, setFirstVisibleIndex] = useState(-1);
  const [lastVisibleIndex, setLastVisibleIndex] = useState(-1);

  const getOrderedItems = useCallback(
    () =>
      [...itemsRef.current].sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1,
      ),
    [],
  );

  const registerItem = useCallback((element: HTMLDivElement) => {
    itemsRef.current.add(element);
    setItemCount(itemsRef.current.size);
    return () => {
      itemsRef.current.delete(element);
      visibleItemsRef.current.delete(element);
      setItemCount(itemsRef.current.size);
    };
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (
      !container ||
      !itemCount ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }
    const items = getOrderedItems();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= VISIBLE_ITEM_RATIO) {
            visibleItemsRef.current.add(entry.target);
          } else {
            visibleItemsRef.current.delete(entry.target);
          }
        }
        const isVisible = (item: Element) => visibleItemsRef.current.has(item);
        setFirstVisibleIndex(items.findIndex(isVisible));
        setLastVisibleIndex(findLastIndex(items, isVisible));
      },
      { root: container, threshold: VISIBLE_ITEM_RATIO },
    );
    for (const item of items) {
      observer.observe(item);
    }
    return () => observer.disconnect();
  }, [scrollRef, itemCount, getOrderedItems]);

  return {
    registerItem,
    getOrderedItems,
    itemCount,
    firstVisibleIndex,
    lastVisibleIndex,
  };
};

export default useSliderItems;
